import React, { useContext, useEffect, useState } from 'react';
import { useParams, useHistory } from 'react-router';
import {
  Box,
  Avatar,
  Divider,
  IconButton,
  ListItemIcon,
  Menu,
  MenuItem,
  Typography
} from '@material-ui/core';
import {
  ExitToAppRounded as LogoutIcon,
  PersonRounded as PersonIcon
} from '@material-ui/icons';
import { useTranslation } from 'react-i18next';

import {
  AuthContext,
  baseUrl,
  EN_US_LOCALE,
  SupportedLocale,
  normalizeLocale,
  setLocaleOverride,
  useFile,
  useSpot
} from 'framework';
import { LocaleSelect } from './locale-select';

interface Membership {
  orgId: string;
  orgName: string;
  orgUserId: string;
  status: 'active' | 'invited' | 'disabled';
  authProvider: 'cognito' | 'google' | 'microsoft';
}

export const SignedInUserMenu = () => {
  const { organization } = useParams<{ organization: string }>();
  const { getCurrentUser, getToken, logout } = useContext(AuthContext);
  const history = useHistory();
  const { t, i18n } = useTranslation();
  const logoutLabel = t('logout');
  const profileLabel = t('profile');
  const openUserMenuLabel = t('openUserMenu');
  const languageLabel = t('language');
  const switchWorkspaceLabel = t('switchWorkspace');
  const switchingWorkspaceLabel = t('switchingWorkspace');

  const id = getCurrentUser();

  const { data, query, command } = useSpot();
  const user = data?.users ? data?.users?.[id] : null;
  const avatar = user?.avatar;
  const initials = `${user?.firstName?.[0] ?? ''}${user?.lastName?.[0] ?? ''}`;

  const gotoUser = () => {
    history.push(`/${organization}/user`);
  };

  const { url } = useFile(avatar || '', organization, id);
  const [menuAnchor, setMenuAnchor] = useState<HTMLElement | null>(null);
  const menuOpen = !!menuAnchor;
  const [memberships, setMemberships] = useState<Membership[]>([]);
  const [switchingOrgId, setSwitchingOrgId] = useState<string | null>(null);

  const openMenu = (event: React.MouseEvent<HTMLElement>) => {
    setMenuAnchor(event.currentTarget);
  };

  const closeMenu = () => {
    setMenuAnchor(null);
  };

  const setLanguage = async (locale: SupportedLocale) => {
    setLocaleOverride(locale);
    await i18n.changeLanguage(locale);
    closeMenu();
  };

  const currentLanguage = normalizeLocale(i18n.language) || EN_US_LOCALE;

  useEffect(() => {
    query(`org/${organization}/user/${id}`, {}, ['users', id]);
  }, [organization, id, query, command]);

  useEffect(() => {
    if (data?.users?.[id] && !data?.users?.[id].verified) {
      command(`org/${organization}/user/${id}/verify`);
    }
  }, [organization, data, id, command]);

  useEffect(() => {
    let active = true;

    const loadMemberships = async () => {
      if (!menuOpen) {
        return;
      }

      setMemberships([]);
      setSwitchingOrgId(null);

      try {
        const token = getToken().getJwtToken();
        const response = await fetch(`${baseUrl}/auth/memberships`, {
          headers: {
            Authorization: `Bearer ${token}`
          }
        });

        if (!response.ok) {
          if (!active) {
            return;
          }
          setMemberships([]);
          return;
        }

        const body = (await response.json()) as {
          memberships?: Membership[];
        };

        if (!active) {
          return;
        }

        setMemberships(
          (body.memberships || []).filter(
            membership => membership.orgId !== organization
          )
        );
      } catch {
        if (!active) {
          return;
        }
        setMemberships([]);
      }
    };

    loadMemberships();

    return () => {
      active = false;
    };
  }, [menuOpen, organization, getToken]);

  const switchWorkspace = async (orgId: string) => {
    setSwitchingOrgId(orgId);

    try {
      const token = getToken().getJwtToken();
      const response = await fetch(`${baseUrl}/auth/select-org`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({ orgId })
      });

      if (!response.ok) {
        setSwitchingOrgId(null);
        return;
      }

      const body = (await response.json()) as {
        token?: string;
      };
      if (!body.token) {
        setSwitchingOrgId(null);
        return;
      }

      localStorage.setItem(`spothr.authToken.${orgId}`, body.token);
      localStorage.removeItem(`spothr.authProvider.${orgId}`);
      localStorage.removeItem(`spothr.testToken.${orgId}`);
      closeMenu();
      window.location.href = `${window.location.origin}/${orgId}`;
    } catch {
      setSwitchingOrgId(null);
    }
  };

  return (
    <>
      <IconButton
        onClick={openMenu}
        aria-label={openUserMenuLabel}
        aria-controls={menuOpen ? 'user-menu' : undefined}
        aria-haspopup="true"
      >
        <Avatar src={url}>{!url ? initials : null}</Avatar>
      </IconButton>
      <Menu
        id="user-menu"
        anchorEl={menuAnchor}
        open={menuOpen}
        onClose={closeMenu}
        keepMounted
      >
        <MenuItem
          onClick={() => {
            closeMenu();
            gotoUser();
          }}
        >
          <ListItemIcon>
            <PersonIcon fontSize="small" />
          </ListItemIcon>
          {profileLabel}
        </MenuItem>
        <Divider />
        <Box px={2} py={1.5} minWidth={180}>
          <Typography color="textSecondary" variant="caption">
            {languageLabel}
          </Typography>
          <Box mt={0.5}>
            <LocaleSelect
              ariaLabel={languageLabel}
              fullWidth
              onChange={setLanguage}
              value={currentLanguage}
            />
          </Box>
        </Box>
        {memberships.length > 0
          ? [
              <Divider key="workspace-divider" />,
              <Box key="workspace-label" px={2} py={1.5} minWidth={180}>
              <Typography color="textSecondary" variant="caption">
                  {switchWorkspaceLabel}
                </Typography>
            </Box>,
              ...memberships.map(membership => (
                <MenuItem
                  key={membership.orgId}
                  disabled={switchingOrgId === membership.orgId}
                  onClick={() => switchWorkspace(membership.orgId)}
                >
                  {switchingOrgId === membership.orgId
                    ? switchingWorkspaceLabel
                    : membership.orgName || membership.orgId}
                </MenuItem>
              ))
            ]
          : null}
        <Divider />
        <MenuItem
          onClick={() => {
            closeMenu();
            logout();
          }}
        >
          <ListItemIcon>
            <LogoutIcon fontSize="small" />
          </ListItemIcon>
          {logoutLabel}
        </MenuItem>
      </Menu>
    </>
  );
};
